import {
  DISPATCH_ACTION_LABELS,
  DISPATCH_ACTIONS,
  DISPATCH_SHORTCUT_ORDER,
  type DispatchAction,
  normalizeKeyBinding,
} from "@storyboard/dispatch-view/view";

/** A key binding split into the key and the modifiers it requires. */
export type ParsedKeyBinding = {
  key: string;
  shift: boolean;
  ctrl: boolean;
  alt: boolean;
  meta: boolean;
};

/** The subset of a keyboard event the matcher reads, so it can be unit-tested without a DOM. */
export type KeyEventLike = Pick<KeyboardEvent, "key" | "shiftKey" | "ctrlKey" | "altKey" | "metaKey">;

const MODIFIERS: Record<string, keyof Omit<ParsedKeyBinding, "key">> = {
  shift: "shift",
  ctrl: "ctrl",
  control: "ctrl",
  alt: "alt",
  opt: "alt",
  option: "alt",
  meta: "meta",
  cmd: "meta",
  command: "meta",
};

/** Spellings a config may use for keys whose `KeyboardEvent.key` differs. */
const KEY_ALIASES: Record<string, string> = {
  esc: "escape",
  return: "enter",
  space: " ",
  up: "arrowup",
  down: "arrowdown",
  left: "arrowleft",
  right: "arrowright",
  pgdn: "pagedown",
  pgup: "pageup",
};

/**
 * Splits a binding such as `Shift+Tab` into its key and modifiers. Comparison is case-insensitive, so `R` and `r`
 * are the same binding; a capital letter never implies Shift.
 */
export function parseKeyBinding(binding: string): ParsedKeyBinding {
  const parsed: ParsedKeyBinding = { key: "", shift: false, ctrl: false, alt: false, meta: false };
  for (const part of normalizeKeyBinding(binding).split("+")) {
    const modifier = MODIFIERS[part];
    if (modifier) {
      parsed[modifier] = true;
      continue;
    }
    parsed.key = KEY_ALIASES[part] ?? part;
  }
  return parsed;
}

/** A key that is itself a shifted character, such as `?` or `!`, so the Shift that typed it is part of the key. */
function isShiftedSymbol(key: string): boolean {
  return key.length === 1 && !/[a-z0-9 ]/i.test(key);
}

/**
 * Whether a keyboard event is exactly the binding: same key, and every modifier present iff the binding names it. A
 * binding to a symbol typed with Shift (`?`) matches whether or not the event reports Shift, since the key already is
 * the shifted character.
 */
export function eventMatchesBinding(event: KeyEventLike, binding: string): boolean {
  const parsed = parseKeyBinding(binding);
  const shiftMatches = isShiftedSymbol(parsed.key) ? true : event.shiftKey === parsed.shift;
  return (
    event.key.toLowerCase() === parsed.key &&
    shiftMatches &&
    event.ctrlKey === parsed.ctrl &&
    event.altKey === parsed.alt &&
    event.metaKey === parsed.meta
  );
}

/** The actions fixed keys also fire, so they answer to more keys than their keymap binding. */
const ALIASED_ACTIONS = ["nextRow", "prevRow", "focusSearch"] as const;

/**
 * The keys that fire an action whatever the page's `keymap` says.
 *
 * The arrows walk the highlight for a hand that reaches for them, J and K for one that stays on the home row, and `/`
 * puts the cursor in the search box — the keys the driver roster answers to
 * (`components/DriverView/keyboard/driverShortcuts.ts`), so the reflex carries from that list to this one. A keymap
 * that binds one of these keys to another action keeps it, since the keymap is matched first; the rest still fire.
 */
const DISPATCH_ACTION_ALIASES: Record<(typeof ALIASED_ACTIONS)[number], readonly string[]> = {
  nextRow: ["ArrowDown", "j"],
  prevRow: ["ArrowUp", "k"],
  focusSearch: ["/"],
};

/** Whether an action is one {@link DISPATCH_ACTION_ALIASES} carries extra keys for. */
function isAliasedAction(action: DispatchAction): action is (typeof ALIASED_ACTIONS)[number] {
  return ALIASED_ACTIONS.some((aliased) => aliased === action);
}

/**
 * The spreadsheet keys that no view rebinds: Shift with an arrow, or with J or K, extends the ticked range as the
 * highlight moves, Ctrl or Cmd with A ticks every row, and Ctrl or Cmd with Z undoes the last acknowledgement.
 */
export type DispatchFixedShortcut = "extendSelectionDown" | "extendSelectionUp" | "selectAll" | "undo";

/** Everything a key can fire: a rebindable action, or one of the fixed selection keys. */
export type DispatchShortcut = DispatchAction | DispatchFixedShortcut;

/**
 * The shortcut a key event fires under a resolved keymap, or `null`. The {@link DISPATCH_ACTION_ALIASES} keys fire
 * their action whatever the keymap says, so a keymap that rebinds `nextRow` never loses arrow or J/K navigation, nor
 * one that rebinds `focusSearch` the `/` key, unless the keymap gives that key to another action; Shift with the row
 * keys extends the ticked range, and with Ctrl/Cmd+A and Ctrl/Cmd+Z undo is fixed the same way.
 */
export function actionForKeyEvent(
  event: KeyEventLike,
  keymap: Record<DispatchAction, string>,
): DispatchShortcut | null {
  for (const action of DISPATCH_ACTIONS) {
    if (keymap[action] !== "" && eventMatchesBinding(event, keymap[action])) return action;
  }
  if (eventMatchesBinding(event, "Shift+ArrowDown") || eventMatchesBinding(event, "Shift+j"))
    return "extendSelectionDown";
  if (eventMatchesBinding(event, "Shift+ArrowUp") || eventMatchesBinding(event, "Shift+k")) return "extendSelectionUp";
  if (eventMatchesBinding(event, "Ctrl+a") || eventMatchesBinding(event, "Cmd+a")) return "selectAll";
  if (eventMatchesBinding(event, "Ctrl+z") || eventMatchesBinding(event, "Cmd+z")) return "undo";
  for (const action of ALIASED_ACTIONS) {
    if (DISPATCH_ACTION_ALIASES[action].some((binding) => eventMatchesBinding(event, binding))) return action;
  }
  return null;
}

/**
 * Every key that fires an action under a resolved keymap: its keymap binding first, then the fixed keys it also
 * answers to and no other action has claimed.
 *
 * The shortcut bar reads this rather than the keymap alone, so what it lists is what {@link actionForKeyEvent} fires.
 *
 * @param action - Only the row keys and the search key carry keys beyond their keymap binding; the rest yield just it.
 * @param keymap - The resolved bindings, one per action; an empty one is an action a collision left unbound.
 */
export function bindingsForAction(action: DispatchAction, keymap: Record<DispatchAction, string>): string[] {
  const own = keymap[action] === "" ? [] : [keymap[action]];
  if (!isAliasedAction(action)) return own;
  const claimed = new Set(DISPATCH_ACTIONS.map((bound) => normalizeKeyBinding(keymap[bound])));
  return [...own, ...DISPATCH_ACTION_ALIASES[action].filter((key) => !claimed.has(normalizeKeyBinding(key)))];
}

/**
 * Whether a key pressed in the search box moves the keyboard into its results: a plain ↓ or Enter, outside an IME
 * composition. Any modifier keeps the key the box's own, so Shift+arrows still select text.
 */
export function entersSearchResults(event: KeyEventLike & Pick<KeyboardEvent, "isComposing">): boolean {
  if (event.isComposing) return false;
  return eventMatchesBinding(event, "ArrowDown") || eventMatchesBinding(event, "Enter");
}

/**
 * Whether a `prevRow` key hands the cursor back to the search box instead of moving the highlight: only the ↑ arrow,
 * only while a search runs, and only from the first row. K and Shift+Tab stop at the first row as they always have, so
 * a run of K presses never types into the query.
 *
 * @param key - The `KeyboardEvent.key` that fired `prevRow`.
 * @param searching - Whether a search term narrows the rows.
 * @param highlightedId - The highlighted row's id, or `null` for none.
 * @param firstRowId - The first row's id, or `null` when the list is empty.
 */
export function prevRowEntersSearch(
  key: string,
  searching: boolean,
  highlightedId: string | null,
  firstRowId: string | null,
): boolean {
  return key === "ArrowUp" && searching && highlightedId != null && highlightedId === firstRowId;
}

/** Keycap text for a binding as the legend shows it, e.g. `Shift+Tab` → ["Shift", "Tab"], `r` → ["R"]. */
export function keycapsForBinding(binding: string): string[] {
  const parsed = parseKeyBinding(binding);
  const caps: string[] = [];
  if (parsed.meta) caps.push("Cmd");
  if (parsed.ctrl) caps.push("Ctrl");
  if (parsed.alt) caps.push("Alt");
  if (parsed.shift) caps.push("Shift");
  const key = parsed.key;
  const KEY_LABELS: Record<string, string> = {
    arrowup: "↑",
    arrowdown: "↓",
    arrowleft: "←",
    arrowright: "→",
    " ": "Space",
    escape: "Esc",
    pagedown: "PgDn",
    pageup: "PgUp",
  };
  caps.push(KEY_LABELS[key] ?? (key.length === 1 ? key.toUpperCase() : key.charAt(0).toUpperCase() + key.slice(1)));
  return caps;
}

/** One shortcut as a list shows it: the action, its label, and a keycap group per key that fires it. */
export type DispatchShortcutEntry = { action: DispatchAction; label: string; keys: string[][] };

/**
 * The shortcuts to list under a resolved keymap, in `DISPATCH_SHORTCUT_ORDER` and then `showShortcuts`.
 *
 * The shortcut bar and the full list both read this, so the two never disagree. An action a collision left with no
 * key is skipped: nothing fires it. The fixed spreadsheet keys are not listed.
 */
export function dispatchShortcutEntries(keymap: Record<DispatchAction, string>): DispatchShortcutEntry[] {
  return [...DISPATCH_SHORTCUT_ORDER, "showShortcuts" as const].flatMap((action) => {
    const bindings = bindingsForAction(action, keymap);
    if (bindings.length === 0) return [];
    return [{ action, label: DISPATCH_ACTION_LABELS[action], keys: bindings.map(keycapsForBinding) }];
  });
}
